/**
 * Publishes the current module's accent hue as the `--accent-module` CSS
 * variable, so nav, chrome and charts subtly retheme as the user moves between
 * modules.
 *
 * The variable is set on `document.documentElement` rather than on a layout
 * element: dialogs, popovers, tooltips and dropdowns render through a portal
 * attached to `<body>`, so anything scoped below the layout wrapper would be
 * skipped by exactly the overlays that most need the accent (their focus rings
 * and active tabs).
 *
 * The token carries no alpha — consumers apply their own via the Tailwind
 * `module-accent` color (`bg-module-accent/15`, `border-module-accent`). See
 * `docs/design-system/ui-navigation.md` § Module accent for the usage rules.
 *
 * The same effect also derives the multi-series chart ramp (`--chart-2`..
 * `--chart-5`) from the module hue, so a chart stays in the module's chromatic
 * family past its first series. `--chart-1` already routes through the accent in
 * `globals.css`; here we publish the remaining four.
 */

import { useEffect } from 'react';
import { useModule } from '../contexts/ModuleContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { getModuleAccentHsl, getModuleChartRamp } from '../utils/color';

const ACCENT_MODULE_VAR = '--accent-module';
const CHART_RAMP_VARS = ['--chart-2', '--chart-3', '--chart-4', '--chart-5'] as const;

export function useModuleAccent(): void {
  const { currentModule } = useModule();
  const { resolvedTheme } = useThemeMode();

  useEffect(() => {
    const isDark = resolvedTheme === 'dark';
    const accent = getModuleAccentHsl(currentModule.color, isDark);
    const chartRamp = getModuleChartRamp(currentModule.color, isDark);
    const root = document.documentElement;

    // Achromatic modules (Admin, User Management) clear the overrides so the
    // theme's own `:root` / `.dark` defaults apply — a desaturated hex has a
    // near-arbitrary hue that would tint the chrome and charts unpredictably.
    if (accent === null) {
      root.style.removeProperty(ACCENT_MODULE_VAR);
    } else {
      root.style.setProperty(ACCENT_MODULE_VAR, accent);
    }

    if (chartRamp === null) {
      CHART_RAMP_VARS.forEach((v) => root.style.removeProperty(v));
    } else {
      CHART_RAMP_VARS.forEach((v, i) => root.style.setProperty(v, chartRamp[i]));
    }

    return () => {
      root.style.removeProperty(ACCENT_MODULE_VAR);
      CHART_RAMP_VARS.forEach((v) => root.style.removeProperty(v));
    };
  }, [currentModule.color, resolvedTheme]);
}
